import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, X, Star } from "lucide-react";

export interface PlanPrice {
  id?: string;
  period: string;
  period_label: string;
  price: string;
  original_price: string;
  is_highlighted: boolean;
  custom_period_months: string;
  order_index: number;
}

const PERIOD_OPTIONS = [
  { value: "monthly", label: "Mensal" },
  { value: "quarterly", label: "Trimestral" },
  { value: "semiannual", label: "Semestral" },
  { value: "annual", label: "Anual" },
  { value: "one_time", label: "Avulso" },
  { value: "custom", label: "Personalizado" },
];

interface PlanPricesEditorProps {
  prices: PlanPrice[];
  onChange: (prices: PlanPrice[]) => void;
}

export function PlanPricesEditor({ prices, onChange }: PlanPricesEditorProps) {
  const addPrice = () => {
    onChange([
      ...prices,
      {
        period: "monthly",
        period_label: "",
        price: "",
        original_price: "",
        is_highlighted: false,
        custom_period_months: "",
        order_index: prices.length,
      },
    ]);
  };

  const updatePrice = (index: number, field: keyof PlanPrice, value: any) => {
    const updated = [...prices];
    (updated[index] as any)[field] = value;
    onChange(updated);
  };

  const removePrice = (index: number) => {
    onChange(prices.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">Preços por Período</Label>

      {prices.length === 0 && (
        <p className="text-sm text-muted-foreground italic py-2">
          Nenhum preço adicionado. Adicione pelo menos um período.
        </p>
      )}

      {prices.map((price, index) => (
        <div
          key={index}
          className="border rounded-lg p-3 space-y-3 relative group"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground font-medium">
              Período {index + 1}
            </span>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <Star
                  className={`w-3.5 h-3.5 ${price.is_highlighted ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground"}`}
                />
                <Switch
                  checked={price.is_highlighted}
                  onCheckedChange={(checked) =>
                    updatePrice(index, "is_highlighted", checked)
                  }
                />
                <span className="text-xs text-muted-foreground">Destacar</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removePrice(index)}
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Período</Label>
              <Select
                value={price.period}
                onValueChange={(v) => updatePrice(index, "period", v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Preço (R$) *</Label>
              <Input
                type="text"
                placeholder="99,90"
                value={price.price}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d,.-]/g, "");
                  updatePrice(index, "price", value);
                }}
                className="h-9"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Preço original (De:)</Label>
              <Input
                type="text"
                placeholder="149,90"
                value={price.original_price}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d,.-]/g, "");
                  updatePrice(index, "original_price", value);
                }}
                className="h-9"
              />
            </div>

            <div>
              <Label className="text-xs">Nome do período (opcional)</Label>
              <Input
                type="text"
                placeholder="Ex: Anual com desconto, Plano Premium"
                value={price.period_label}
                onChange={(e) =>
                  updatePrice(index, "period_label", e.target.value)
                }
                className="h-9"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Sobrescreve o nome padrão do período (Mensal, Anual, etc.) na exibição ao cliente.
              </p>
            </div>
          </div>

          {price.period === "custom" && (
            <div>
              <Label className="text-xs">Duração (meses)</Label>
              <Input
                type="number"
                min="1"
                placeholder="Ex: 18"
                value={price.custom_period_months}
                onChange={(e) =>
                  updatePrice(index, "custom_period_months", e.target.value)
                }
                className="h-9"
              />
            </div>
          )}
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={addPrice}
        disabled={prices.length >= 10}
        className="w-full border-dashed"
      >
        <Plus className="w-4 h-4 mr-2" /> Adicionar período
      </Button>
    </div>
  );
}
